import { useEffect, useMemo, useRef, useState } from "react";
import { useAppState } from "../context/AppContext";
import { FormProvider, useForm } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { usePractice } from "../usePractice";
import { usePracticeSettings } from "../usePracticeSettings";
import { useSubmitterNames } from "../useSubmitterNames";
import { useUserProfile } from "../useUserProfile";
import {
  FORM_FIELD_KEYS,
  isFormFieldEnabled,
  isPrintFromDraftEnabled,
} from "../config/practiceControls";
import { buildReturnLogisticsOptions } from "../config/returnLogisticsOptions";
import { getBrand } from "../utils/brand";
import { functions, storage } from "../services/firebase";
import { httpsCallable } from "firebase/functions";
import { filename } from "../services/filename";
import { openBodyBagTagPDF } from "../services/bodyBagTag";
import TextField from "../ui/TextField";
import SingleSelectField from "../ui/SingleSelectField";
import PhoneField from "../ui/PhoneField";
import CommunicationPrefField from "../ui/CommunicationPrefField";
import RemembranceItems from "../ui/RemembranceItems";
import TextAreaField from "../ui/TextAreaField";
import EuthanasiaFormField from "../ui/EuthanasiaFormField";
import SignatureBox from "../ui/SignatureBox";
import DropdownField from "../ui/DropdownField";
import AddressField from "../ui/AddressField";
import SpeciesField from "../ui/SpeciesField";
import debounce from "lodash.debounce";
import { realtimeDb } from "../services/firebase";
import { ref, set, get, remove } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmSubmitModal } from "../components/ConfirmSubmitModal";

function PracticeIntakeForm() {
  const queryClient = useQueryClient();
  const { state } = useAppState();
  const navigate = useNavigate();
  const { draftId: routeDraftId, slug } = useParams();
  const userId = state.user?.uid;
  const processNewCase = httpsCallable(functions, "processNewCase");
  const { practice, isLoadingPractice } = usePractice();
  const { settings, isLoading: isLoadingSettings } = usePracticeSettings(slug);
  const { names: submitterNames, rememberName } = useSubmitterNames(slug);
  const { name: userName } = useUserProfile();
  const brand = getBrand(practice?.fields?.brand);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrintingTag, setIsPrintingTag] = useState(false);
  const [tagReminder, setTagReminder] = useState(false);
  const sigCanvas = useRef({});
  const [signatureIsEmpty, setSignatureIsEmpty] = useState(true);
  const [draftId, setDraftId] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const urnOptions = practice?.fields?.urnOptions?.map((u) => {
    return { label: u.split("|")[0], value: u };
  });

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      ownerName: "",
      ownerTwoName: "",
      emailPreferred: true,
      smsPreferred: false,
      phonePreferred: false,
      excludeRemembranceItems: false,
      clayImpressions: 1,
      inkPrints: 1,
      furClippings: 1,
    },
  });
  const { handleSubmit, watch, setValue, getValues } = methods;
  const aquamationType = watch("aquamationType");
  const returnLogistics = watch("returnLogistics");
  const species = watch("species");
  const tagNumber = watch("tagNumber");

  // 0 is a real choice here — it means no tag was provided, and the tag prints a
  // blank to fill in by hand — so only an empty or invalid entry blocks printing.
  const canPrintBodyBagTag =
    tagNumber !== null &&
    tagNumber !== undefined &&
    String(tagNumber).trim() !== "" &&
    Number.isFinite(Number(tagNumber)) &&
    Number(tagNumber) >= 0;

  const aquamationTypes = useMemo(() => {
    const types = [
      { label: "Remains Returned", value: "Remains Returned" },
      { label: "Scattering Service", value: "Scattering Service" },
      { label: "Undecided", value: "Undecided" },
    ];
    if (practice?.fields?.allowPrivateAquamation)
      types.push({ label: "Private", value: "Private" });
    return types;
  }, [practice?.fields?.allowPrivateAquamation]);

  const returnLogisticsOptions = useMemo(
    () => buildReturnLogisticsOptions(practice?.fields, settings),
    [practice?.fields, settings]
  );
  const printFromDraft = isPrintFromDraftEnabled(settings);

  // Practices whose staff each sign in as themselves turn this off, and the tag
  // falls back to the signed-in user instead of asking a redundant question.
  const showSubmitterInfo = isFormFieldEnabled(
    settings,
    FORM_FIELD_KEYS.SUBMITTER_INFO
  );

  /**
   * Who the case is submitted by, as printed on the tag and saved with the
   * case: what was typed when the field is on, the signed-in user when it's off.
   *
   * A tag can be printed from a draft before the field is filled in. That prints
   * without a submitter row rather than falling back to the signed-in user —
   * a practice that asks the question is one where the two aren't the same
   * person, so guessing would put the wrong name on the tag.
   */
  function resolveSubmitterInfo(typedValue) {
    if (!showSubmitterInfo) return userName || "";
    return (typedValue || "").trim();
  }

  function fillDevData() {
    methods.reset({
      petName: "Fluffy",
      species: "Cat",
      weight: 9,
      age: 7,
      sex: "Female",
      breed: "Tabby",
      furColor: "Brown tabby",
      uniqueIdentifiers: "Brown spot on belly",
      ownerName: "Linda Harris",
      ownerEmail: "linda@example.com",
      ownerPhone: "+12065551234",
      emailPreferred: true,
      smsPreferred: false,
      phonePreferred: false,
      ownerTwoName: "Andy Harris",
      ownerTwoEmail: "andy@example.com",
      ownerTwoPhone: "+12065554321",
      aquamationType: "Remains Returned",
      excludeRemembranceItems: false,
      clayImpressions: 1,
      inkPrints: 1,
      furClippings: 1,
      tagNumber: 1002,
      submitterInfo: "Dr. Alice Nguyen",
      specialInstructions:
        "Please play her favorite song during the aquamation: 'Eye of the Tiger by Survivor'",
      returnLogistics: "Return to primary care clinic",
      urnSelection: "Scatter Vessel: No Liner|recUgLPoM1rPnPubx",
    });
  }

  async function uploadEuthanasiaForm(filenameString) {
    const form = document.getElementById("euthanasia-authorization-form");
    const file = form.files[0];

    if (file) {
      const fileExtension = file.name.split(".").pop();
      const euthanasiaRef = storageRef(
        storage,
        `/public/euthanasia-pdfs/${filenameString}.${fileExtension}`
      );
      await uploadBytes(euthanasiaRef, file);
      return {
        filename: `${filenameString}.${fileExtension}`,
        url: await getDownloadURL(euthanasiaRef),
      };
    }
    return null;
  }

  useEffect(() => {
    if (species !== "Other") {
      setValue("otherSpecies", "");
    }
  }, [species, setValue]);

  // An urn is only offered for "Remains Returned", so drop any earlier selection
  // when the type changes — a stale one is hidden but still reaches the case,
  // the PDFs and the Shopify order.
  useEffect(() => {
    if (aquamationType !== "Remains Returned" && getValues("urnSelection")) {
      setValue("urnSelection", "");
    }
  }, [aquamationType, getValues, setValue]);

  // Load draft on mount if routeDraftId is present
  useEffect(() => {
    if (!userId || !practice?.fields?.slug || !routeDraftId) return;
    const draftRef = ref(realtimeDb, `/caseDrafts/${userId}/${routeDraftId}`);
    get(draftRef).then((snapshot) => {
      const draft = snapshot.val();
      if (draft) {
        setDraftId(routeDraftId);
        methods.reset(draft.formData);
      }
    });
  }, [userId, practice?.fields?.slug, routeDraftId]);

  // Utility to sanitize form data (replace undefined with null)
  function sanitizeFormData(data) {
    const sanitized = {};
    for (const key in data) {
      if (data[key] === undefined) {
        sanitized[key] = null;
      } else {
        sanitized[key] = data[key];
      }
    }
    return sanitized;
  }

  // Debounced save draft
  const debouncedSaveDraft = useMemo(
    () =>
      debounce((data) => {
        if (!userId || !practice?.fields?.slug || isSubmitting) return;
        let id = draftId || routeDraftId;
        if (!id) {
          id = uuidv4();
          setDraftId(id);
        }
        const draftRef = ref(realtimeDb, `/caseDrafts/${userId}/${id}`);
        set(draftRef, {
          formData: sanitizeFormData(data),
          lastUpdated: Date.now(),
          draftId: id,
          practiceSlug: practice.fields.slug,
        }).then(() => {
          setDraftSaved(true);
          window.history.replaceState(
            null,
            "",
            `/${practice.fields.slug}/draft/${id}`
          );
        });
      }, 1500),
    [userId, practice?.fields?.slug, draftId, isSubmitting]
  );

  // Watch form changes and save draft unless submitting in progress
  useEffect(() => {
    if (isSubmitting) return;
    const subscription = methods.watch((value) => {
      debouncedSaveDraft(value);
      setDraftSaved(false);
    });
    return () => subscription.unsubscribe();
  }, [methods, debouncedSaveDraft, isSubmitting]);

  // Show 'Draft saved' indicator for 2 seconds
  useEffect(() => {
    if (draftSaved) {
      const timer = setTimeout(() => setDraftSaved(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [draftSaved]);

  // A full-sentence reminder needs longer to read than the draft indicator.
  useEffect(() => {
    if (tagReminder) {
      const timer = setTimeout(() => setTagReminder(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [tagReminder]);

  useEffect(() => {
    if (returnLogistics) {
      setValue("shippingAddress", null);
      setValue("primaryCareClinicAddress", null);
      setValue("primaryCareClinicName", null);
    }
  }, [returnLogistics, setValue]);

  async function printBodyBagTag() {
    setIsPrintingTag(true);
    try {
      const data = getValues();
      await openBodyBagTagPDF({
        tagNumber: data.tagNumber,
        petName: data.petName,
        species: data.species,
        otherSpecies: data.otherSpecies,
        furColor: data.furColor,
        weight: data.weight,
        aquamationType: data.aquamationType,
        ownerName: data.ownerName,
        submitterInfo: resolveSubmitterInfo(data.submitterInfo),
        practiceName: practice.fields.practiceName,
        brand,
      });
      setTagReminder(true);
    } catch {
      // Error already surfaced in openBodyBagTagPDF
    } finally {
      setIsPrintingTag(false);
    }
  }

  // Validation runs first, then the confirmation modal holds the validated
  // values until the user says the case is right.
  function requestSubmit(data) {
    setPendingSubmission(data);
  }

  function confirmSubmit(familyEmailDelayHours) {
    const data = pendingSubmission;
    setPendingSubmission(null);
    onSubmit({
      ...data,
      // 0 sends the family email with the case. 6, 12, or 24 hold only that
      // Klaviyo email; the case itself is still created immediately.
      familyEmailDelayHours: Number(familyEmailDelayHours) || 0,
    });
  }

  async function onSubmit(data) {
    setIsSubmitting(true);

    data.ownerName = data.ownerName.trim();
    if (data.ownerTwoName) {
      data.ownerTwoName = data.ownerTwoName.trim();
    }
    // Cancel any pending draft saves
    debouncedSaveDraft.cancel();
    // Convert data to correct format and add additional fields
    data.practice = new Array(practice.id);
    data.weight = parseInt(data.weight);
    data.age = String(data.age);
    data.tagNumber = parseInt(data.tagNumber);
    data.practiceName = practice.fields.practiceName;
    data.signatureUrl = sigCanvas.current.toDataURL("image/png");
    data.signedBy = state.user.name;
    data.formReceiptEmail = practice.fields.formReceiptEmail;
    data.formSubmissionTime = new Date();
    data.testMode = process.env.REACT_APP_ENV === "staging";
    data.submitterType = "Practice Employee";
    data.submitterInfo = resolveSubmitterInfo(data.submitterInfo);
    // Offer this name to the rest of the practice next time. Never let the
    // memory fail a submission — the case matters, the suggestion doesn't.
    if (showSubmitterInfo && data.submitterInfo) {
      try {
        await rememberName(data.submitterInfo);
      } catch (error) {
        console.log("Could not remember submitter name", error);
      }
    }
    data.filenameString = filename(data.petName);
    data.brand = brand;
    data.aquatory = practice.fields.defaultAquatory;
    data.aquatoryPrinterEmail = practice.fields.aquatoryPrinterEmail;

    // Upload files and get urls
    data.euthanasiaFile = await uploadEuthanasiaForm(data.filenameString);

    // Send to firebase for case creation
    try {
      const res = await processNewCase(data);
      if (res.data.error) {
        alert("There was an error submitting the form. Please try again.");
        setIsSubmitting(false);
        return;
      }
      // Delete draft
      if (userId && draftId) {
        const draftRef = ref(realtimeDb, `/caseDrafts/${userId}/${draftId}`);
        await remove(draftRef);
        setDraftId(null);
      }
      // Add case to query cache
      queryClient.setQueryData(["case", res.data.id], res.data);
      // Bust list cache
      queryClient.invalidateQueries(["cases"]);
      window.scrollTo(0, 0);
      navigate(`/${practice.fields.slug}/case/${res.data.id}`);
    } catch (error) {
      console.log(error);
      alert("There was an error submitting the form. Please try again.");
      setIsSubmitting(false);
    }
  }

  if (isLoadingPractice || isLoadingSettings) {
    return <div className="loading-screen regular-2xl">Loading...</div>;
  }

  if (!practice?.fields) {
    return (
      <div className="container">
        <p className="input-error">
          Could not load this practice. Please refresh and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      <Helmet>
        <title>{`${practice?.fields?.brand || "Evvi"} - New case`}</title>
      </Helmet>

      {isSubmitting && (
        <div className="loading-screen regular-2xl">Submitting...</div>
      )}

      <h2 className="mb-2 text-center">Create new case</h2>

      {process.env.NODE_ENV === "development" && (
        <button
          onClick={(e) => {
            e.preventDefault();
            fillDevData();
          }}
        >
          Fill with dev data
        </button>
      )}

      {draftSaved && <div className="draft-saved-indicator">Draft saved</div>}

      {tagReminder && (
        <div
          className="tag-reminder-toast"
          role="status"
          onClick={() => setTagReminder(false)}
          data-cy="tag-reminder-toast"
        >
          Body bag tag created. Please remember to submit the case before your
          transportation day.
        </div>
      )}

      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(requestSubmit)}
          data-cy="practice-intake-form"
        >
          <h3 className="text-center">Pet Info</h3>

          <div className="fieldgroup">
            <TextField name="petName" label="Pet Name" required />
            <TextField
              name="weight"
              label="Weight (lbs)"
              type={"number"}
              required
            />
            <SingleSelectField
              name="sex"
              label="Sex"
              options={[
                { label: "Male", value: "Male" },
                { label: "Female", value: "Female" },
              ]}
              required
            />
            <SpeciesField />
            {species === "Other" && (
              <TextField name="otherSpecies" label="Species Detail" required />
            )}
            <TextField name="breed" label="Breed" required />
            <TextField
              name="furColor"
              label="Fur/Body Color"
              required
              placeholder="Black, brown tabby, golden, etc."
            />
            <TextField
              name="uniqueIdentifiers"
              label="Unique Identifiers"
              placeholder="Tripod, polydactyl, distinctive markings, etc."
            />
            <TextField name="age" label="Approximate Age (yrs)" required />
          </div>

          <h3 className="mt-2 text-center">Owner Info</h3>

          <div className="fieldgroup">
            <TextField
              name="ownerName"
              label="Full Name"
              required
              validation={(value) =>
                value.trim() === "" ||
                value.trim().split(" ").length > 1 ||
                "Please enter owner's full name"
              }
            />
            <TextField name="ownerEmail" label="Email" type="email" required />
            <div>
              <PhoneField name="ownerPhone" label="Phone" required />
            </div>
            <CommunicationPrefField />
          </div>

          <h3 className="mt-2 text-center">2nd Owner Info</h3>

          <div className="fieldgroup">
            <TextField
              name="ownerTwoName"
              label="Full Name"
              validation={(value) =>
                value.trim() === "" ||
                value.trim().split(" ").length > 1 ||
                "Please enter second owner's full name"
              }
            />
            <TextField name="ownerTwoEmail" label="Email" type="email" />
            <PhoneField name="ownerTwoPhone" label="Phone" />
          </div>

          <h3 className="mt-2 text-center">Aquamation Details</h3>

          <div className="fieldgroup">
            <SingleSelectField
              name="aquamationType"
              label="Aquamation Type"
              options={aquamationTypes}
              fullWidth
              required
            />
            <RemembranceItems />
            {aquamationType === "Remains Returned" &&
              practice?.fields?.enableReturnLogistics &&
              returnLogisticsOptions.length > 0 && (
                <DropdownField
                  name="returnLogistics"
                  label="Return Logistics"
                  required
                  requiredText="This field is required"
                  options={returnLogisticsOptions}
                />
              )}
            {returnLogistics === "Ship to pet family" && (
              <AddressField
                name="shippingAddress"
                label="Shipping Address"
                required
              />
            )}
            {returnLogistics === "Return to primary care clinic" && (
              <>
                <TextField
                  name="primaryCareClinicName"
                  label="Primary Care Clinic Name"
                  required
                />
                <AddressField
                  name="primaryCareClinicAddress"
                  label="Primary Care Clinic Address"
                  required
                />
              </>
            )}
            {aquamationType === "Remains Returned" && urnOptions && (
              <DropdownField
                name="urnSelection"
                label="Urn Choice"
                required
                requiredText="This field is required"
                options={urnOptions}
              />
            )}
            <TextField
              name="tagNumber"
              label="Tag Number"
              type={"number"}
              required
              requiredText="This field is required. Enter 0 if no tag is provided."
              validation={(value) =>
                value >= 0 ||
                "Tag numbers are greater than 0. Enter 0 if no tag is provided."
              }
            />
            <TextAreaField
              name="specialInstructions"
              label="Special Instructions"
              calloutVariant="shared"
              callout={<>These instructions are emailed to the pet owners.</>}
              placeholder="Requests from the family, how they'd like their pet remembered, etc."
            />

            <EuthanasiaFormField />

            <TextAreaField
              name="operationalInstructions"
              label="Operational Instructions"
              calloutVariant="internal"
              callout={
                <>
                  <strong>Internal only.</strong> Only our staff will receive
                  these instructions.
                </>
              }
              placeholder="Pet pickup coordination, remains pickup instructions, etc."
            />

            {showSubmitterInfo && (
              <TextField
                name="submitterInfo"
                label="Submitter Info"
                required
                suggestions={[...submitterNames].sort((a, b) =>
                  a.localeCompare(b)
                )}
              />
            )}

            <SignatureBox
              canvasRef={sigCanvas}
              setSignatureIsEmpty={setSignatureIsEmpty}
            />
          </div>

          <div className="mt-2 text-center">
            {printFromDraft && (
              <button
                type="button"
                className="button button-black-700 spaced"
                onClick={printBodyBagTag}
                disabled={!canPrintBodyBagTag || isPrintingTag}
                data-cy="print-body-bag-tag-button"
              >
                {isPrintingTag ? "Preparing tag..." : "Print body bag tag"}
              </button>
            )}
            <Link
              to={`/${practice?.fields?.slug}/?tab=drafts`}
              className="button button-yellow-900 spaced"
              data-cy="save-draft-button"
            >
              Save as Draft
            </Link>
            <button
              className="button"
              type="submit"
              disabled={signatureIsEmpty}
              data-cy="submit-button"
            >
              Submit
            </button>
          </div>
        </form>
      </FormProvider>

      {pendingSubmission && (
        <ConfirmSubmitModal
          data={pendingSubmission}
          brand={brand}
          onCancel={() => setPendingSubmission(null)}
          onConfirm={confirmSubmit}
        />
      )}
    </div>
  );
}

export default PracticeIntakeForm;
